import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import firebase from 'src/utils/firebase';
import { getActiveShopId } from 'src/utils/shopContext';
import { socketClient } from '../index';

export const currentServiceAdvisorScope = () => {
  const uid = firebase.auth().currentUser?.uid;
  return uid ? `${uid}:${getActiveShopId() || ''}` : null;
};

// Work Order pages load advisors once and may mount during an automatic socket
// reconnect. Reads can wait for that captured connection; mutations never queue.
const waitForAdvisorReadConnection = (connection, idToken, scope, signal) =>
  new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timeoutId);
      connection.off('connect', handleConnect);
      connection.off('connect_error', handleError);
      signal.removeEventListener('abort', handleAbort);
    };
    const fail = (message) => {
      cleanup();
      reject(new Error(message));
    };
    const handleConnect = () => {
      if (signal.aborted || currentServiceAdvisorScope() !== scope
        || socketClient.socket !== connection || socketClient.authToken !== idToken
        || Number(socketClient.shopId) !== Number(getActiveShopId())) {
        fail('Account or connection changed. Refresh service advisors.');
        return;
      }
      if (!connection.connected) return;
      cleanup();
      resolve();
    };
    const handleError = () => fail('Unable to connect while loading service advisors.');
    const handleAbort = () => fail('Service advisor request cancelled.');
    const timeoutId = setTimeout(() => fail('Timed out while connecting to load service advisors.'), 10000);
    connection.on('connect', handleConnect);
    connection.on('connect_error', handleError);
    signal.addEventListener('abort', handleAbort);
    if (signal.aborted) handleAbort();
  });

const emitScopedServiceAdvisorsRequest = ({
  emitEvent, payload, timeoutMessage, scope, signal, connection
}) => new Promise((resolve, reject) => {
  const cleanup = () => {
    clearTimeout(timeoutId);
    connection.off('getServiceAdvisors', handleSuccess);
    connection.off('getServiceAdvisorsError', handleError);
    connection.off('disconnect', handleDisconnect);
    signal.removeEventListener('abort', handleAbort);
  };
  const fail = (message) => {
    cleanup();
    reject(new Error(message));
  };
  const handleSuccess = (response) => {
    if (response?.requestId !== payload.requestId) return;
    if (currentServiceAdvisorScope() !== scope) {
      fail('Account changed. Refresh service advisors.');
      return;
    }
    const advisors = Array.isArray(response) ? response : response?.serviceAdvisors;
    if (!Array.isArray(advisors)) {
      fail('Unable to read service advisor status. Refresh and try again.');
      return;
    }
    cleanup();
    resolve(advisors);
  };
  const handleError = (response) => {
    if (response?.requestId !== payload.requestId) return;
    fail(currentServiceAdvisorScope() === scope
      ? response?.message || timeoutMessage
      : 'Account changed. Refresh service advisors.');
  };
  const handleAbort = () => fail('Service advisor request cancelled.');
  const handleDisconnect = () => fail('Realtime connection changed. Refresh status before trying again.');
  const timeoutId = setTimeout(() => fail(timeoutMessage), 10000);
  connection.on('getServiceAdvisors', handleSuccess);
  connection.on('getServiceAdvisorsError', handleError);
  connection.on('disconnect', handleDisconnect);
  signal.addEventListener('abort', handleAbort);
  if (signal.aborted) {
    handleAbort();
    return;
  }
  try {
    // Send synchronously on this authenticated connection; do not queue a mutation
    // through SocketClient.emit across reconnects or account changes.
    connection.emit(emitEvent, payload);
  } catch (error) {
    fail(error.message || timeoutMessage);
  }
});

const initialState = {
  serviceAdvisors: [],
  loading: false,
  error: null,
  scope: null,
  requestId: null
};

const advisorRequest = (event, timeoutMessage, buildPayload = (value) => value || {}) =>
  createAsyncThunk(`serviceAdvisors/${event === 'getServiceAdvisors' ? 'fetchServiceAdvisors' : event}`, async (value, { rejectWithValue, requestId, signal }) => {
    const scope = currentServiceAdvisorScope();
    try {
      if (!scope) throw new Error('Sign in to manage service advisors.');
      const idToken = await firebase.auth().currentUser.getIdToken();
      // Never send an old form or response into a new account/shop after token hydration.
      if (signal.aborted || currentServiceAdvisorScope() !== scope) {
        throw new Error('Account changed. Refresh service advisors.');
      }
      const connection = socketClient.socket;
      if (event === 'getServiceAdvisors' && connection && !connection.connected) {
        await waitForAdvisorReadConnection(connection, idToken, scope, signal);
      }
      // Recheck after any read-only wait; its connection/account may have changed.
      if (signal.aborted || currentServiceAdvisorScope() !== scope
        || socketClient.socket !== connection || !connection?.connected || socketClient.authToken !== idToken
        || Number(socketClient.shopId) !== Number(getActiveShopId())) {
        throw new Error('Realtime connection is not ready for this account. Refresh status after reconnecting.');
      }
      const advisors = await emitScopedServiceAdvisorsRequest({
        emitEvent: event,
        payload: { ...buildPayload(value), idToken, requestId },
        timeoutMessage, scope, signal, connection
      });
      if (currentServiceAdvisorScope() !== scope) {
        throw new Error('Account changed. Refresh service advisors.');
      }
      return advisors;
    } catch (error) {
      return rejectWithValue(error.message || timeoutMessage);
    }
  }, {
    getPendingMeta: () => ({ scope: currentServiceAdvisorScope() }),
    // Prevent repeated saves/retries, including before React paints disabled buttons.
    condition: (_, { getState }) => event === 'getServiceAdvisors'
      || !getState().serviceAdvisors?.loading
  });

export const fetchServiceAdvisors = advisorRequest('getServiceAdvisors', 'Timed out while loading service advisors');
export const createServiceAdvisor = advisorRequest('createServiceAdvisor', 'Timed out while creating service advisor. Retry this form to reuse the same save request, or refresh advisors before starting another create.');
export const editServiceAdvisor = advisorRequest('editServiceAdvisor', 'Timed out while updating service advisor');
export const deleteServiceAdvisor = advisorRequest('deleteServiceAdvisor', 'Timed out while deleting service advisor', (id) => ({ id }));
export const retryServiceAdvisorPhoneSetup = advisorRequest('retryServiceAdvisorPhoneSetup', 'Timed out while retrying phone setup. Refresh status before trying again.', (id) => ({ id }));

const slice = createSlice({
  name: 'serviceAdvisors',
  initialState,
  reducers: {
    clearServiceAdvisors: () => initialState
  },
  extraReducers: (builder) => {
    [fetchServiceAdvisors, createServiceAdvisor, editServiceAdvisor, deleteServiceAdvisor, retryServiceAdvisorPhoneSetup].forEach((request) => {
      builder
        .addCase(request.pending, (state, action) => {
          if (state.scope !== action.meta.scope) state.serviceAdvisors = [];
          state.scope = action.meta.scope;
          state.requestId = action.meta.requestId;
          state.loading = true;
          state.error = null;
        })
        .addCase(request.fulfilled, (state, action) => {
          if (state.requestId !== action.meta.requestId) return;
          state.serviceAdvisors = action.payload;
          state.loading = false;
          state.error = null;
          state.requestId = null;
        })
        .addCase(request.rejected, (state, action) => {
          if (state.requestId !== action.meta.requestId) return;
          state.loading = false;
          state.error = action.meta.aborted ? null : action.payload || action.error.message;
          state.requestId = null;
        });
    });
  }
});

export const { clearServiceAdvisors } = slice.actions;
export const reducer = slice.reducer;
